<template>
  <div v-if="!completed" class="container-fluid mt-3">
    <div class="row">
      <div class="col-8 iframe-container">
        <iframe
          src=""
          height="800px"
          width="100%"
          :id="iframeId"
          allow="autoplay; fullscreen; vr"
          allowvr
          allowfullscreen
          mozallowfullscreen="true"
          webkitallowfullscreen="true"
        ></iframe>
        <SketchfabControls />
      </div>
      <div class="col-4">
        <div class="question-wrapper" v-show="dataFetched">
          <InstructionsBlock />
          <LoadingAlert v-show="!loaded" />
          <AnswerBlock
            v-show="loaded"
            @checkAnswer="checkAnswer($event)"
            :answerSet="answerSet"
          />
          <div
            v-if="showAnswerCorrect"
            class="alert alert-success text-dark d-flex justify-content-between"
          >
            <span class="h3 fw-bold m-0 p-0">Correct!</span>
            <button class="btn btn-success" @click="nextPrompt()">
              Next
            </button>
          </div>
          <div
            v-if="showAnswerIncorrect"
            class="alert alert-danger text-dark d-flex justify-content-between"
          >
            <span class="h3 fw-bold m-0 p-0">Incorrect. Try again.</span>
          </div>
        </div>
        <PromptProgress
          v-show="dataFetched"
          :current="current"
          :total="shuffledItems.length"
        />
        <DebugPanel
          v-if="debugMode"
          :correct="shuffledItems[promptId]?.name"
          :lastClicked="lastClicked"
          :clickedCorrect="answerCorrect"
          :distractors="shuffledItems[promptId]?.distractors"
          :shuffledNames="shuffledNames"
        />
      </div>
    </div>
  </div>
  <CompletionAlert v-if="completed" />
</template>

<script>
// @ is an alias to /src

import CompletionAlert from "@/components/CompletionAlert";
import LoadingAlert from "@/components/LoadingAlert";
import PromptProgress from "@/components/PromptProgress";
import DebugPanel from "@/components/DebugPanel";
import AnswerBlock from "@/components/AnswerBlock";
import SketchfabControls from "@/components/SketchfabControls";
import InstructionsBlock from "@/components/InstructionsBlock";

export default {
  name: "Main",
  components: {
    CompletionAlert,
    LoadingAlert,
    PromptProgress,
    DebugPanel,
    AnswerBlock,
    SketchfabControls,
    InstructionsBlock
  },
  data: () => {
    return {
      iframeId: "api-frame",
      api: null,
      dataFetched: false,
      bgColor: [0.1, 0.1, 0.1],
      highlightColor: [0.3, 0.3, 0.3],
      promptId: 0,
      lastClicked: null, // last answer clicked,
      debugMode: Boolean(parseInt(process.env.VUE_APP_DEBUG_MODE)),
      sourceId: process.env.VUE_APP_CONTENT_SHEET,
      loaded: false,
      answerCorrect: false,
      items: []
    };
  },
  computed: {
    modelId() {
      return this.shuffledItems[this.promptId].model_id;
    },
    current() {
      return this.promptId + 1;
    },
    completed() {
      return this.promptId > 0 && this.promptId == this.shuffledItems.length;
    },
    shuffledNames() {
      // used just for debug
      return this.shuffledItems.map(e => e.name);
    },
    showAnswerIncorrect() {
      return this.lastClicked !== null && this.answerCorrect == false;
    },
    showAnswerCorrect() {
      return this.lastClicked !== null && this.answerCorrect == true;
    },
    answerSet() {
      if (!this.shuffledItems.length) {
        return [];
      }

      let answers = [];

      // Add Distractors
      console.log(this.shuffledItems[this.promptId]);
      answers = answers.concat(this.shuffledItems[this.promptId].distractors);
      answers = this.shuffle(answers); // shuffle all possible answers
      answers = answers.slice(0, 3); // slice to one less than max

      //console.log("Post Distractors", answers.length);

      // if we need more, add more randomly
      for (let x = answers.length; x < 3; x += 1) {
        answers.push(this.getPossibleAnswer(answers, this.shuffledItems));
      }

      // Add Correct Answer
      //console.log("Adding correct", this.shuffledItems[this.promptId].name);
      answers.unshift(this.shuffledItems[this.promptId].name);

      // Shuffle answers as long as we're not in debug mode.
      if (!this.debugMode) {
        answers = this.shuffle(answers);
      }

      return answers;
    },
    formattedData() {
      let items = this.itemsWithModels.map(item => {
        let i = {};
        i.name = item.label ? item.label : item.name;
        i.model_id = item.model_id;

        let distractors = item.distractors.split(",");
        i.distractors = distractors.map(distractor => {
          return distractor.trim();
        });

        return i;
      });

      return items;
    },
    itemsWithModels() {
      let items = this.items.filter(item => item.model_id);
      return items;
    },
    shuffledItems() {
      return this.shuffle(this.formattedData);
    }
  },

  /*
  this.items
  itemsWithModels // are models missing?
  formattedData // parse distractors, rename if label is present
  shuffledItems
  answerSet
    - add distractors
      - shuffle and slice
    - getPossibleAnswer
    - add correct answer
    - shuffle (if in prod)
  */
  methods: {
    getPossibleAnswer(existingAnswers, answerPool) {
      // Map answerPool Objects to just names
      let names = answerPool.map(e => e.name);

      // Remove existing answers from the array
      let possible = names.filter(e => !existingAnswers.includes(e));

      let answers = this.shuffle(possible);
      return answers[0];
    },
    setBackground() {
      // had to use separator, conflict with 'this' scope.
      this.api.setBackground({ color: { ...this.bgColor } });
    },

    checkAnswer(answer) {
      this.lastClicked = answer;

      if (answer === this.shuffledItems[this.promptId].name) {
        this.answerCorrect = true;
      } else {
        this.answerCorrect = false;
      }
    },
    nextPrompt() {
      this.promptId += 1;
      this.loaded = false;
      this.lastClicked = null;
      this.renderModel();
    },
    shuffle(array) {
      var currentIndex = array.length,
        randomIndex;

      // While there remain elements to shuffle...
      while (0 !== currentIndex) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
          array[randomIndex],
          array[currentIndex]
        ];
      }

      return array;
    },
    renderModel() {
      let ref = this; // Assigning reference, because 'this' is being overwritten by sketchfab

      const iframe = document.getElementById(this.iframeId);
      const client = new window.Sketchfab(iframe);

      client.init(this.modelId, {
        api_log: 1,
        ui_fullscreen: 0,
        ui_watermark_link: 0,
        ui_watermark: 0,
        ui_general_controls: 0,
        ui_hint: 0,
        ui_animations: 0,
        ui_annotations: 0,
        ui_sound: 0,
        ui_settings: 0,
        ui_controls: 0,
        ui_help: 0,
        ui_infos: 0,
        annotations_visible: 0,
        annotation: 0,
        annotation_cycle: 0,
        annotation_tooltip_visible: 0,

        success: function onSuccess(api) {
          ref.api = api;

          console.log("[Vue] Success");
          api.load();
          api.start();

          ref.api.addEventListener("viewerready", function() {
            console.log("Viewer is ready");
            // once the viewer is ready, show the iframe

            iframe.classList.remove("hidden"); // Remove hidden class

            ref.setBackground();
            ref.loaded = true;
          });
        },
        error: function onError(e) {
          console.log("Error", e);
        }
      });
    }
  },
  created() {
    this.axios.get(process.env.VUE_APP_CONTENT_ENDPOINT).then(response => {
      this.items = response.data.message;
      this.dataFetched = true;
      this.renderModel();
    });
  }
};
</script>

<style scoped>
.iframe-container {
  position: relative;
}
</style>
